import type {
  ChatResponse,
  Conversation,
  FeedbackSubmission,
  Feedback,
  RagDocument,
  RagDocumentsResponse,
  RagSyncResponse,
  RagSyncJobStatus,
  RagSearchResponse,
  RagChunksResponse,
  RagStatsResponse,
  RagDocumentDetails,
  RagConfigResponse,
  RagCostsResponse,
  RagDocumentCostsResponse,
  // Testing types
  TestQuestion,
  TestSet,
  TestSetWithQuestions,
  TestRunWithResults,
  TestQuestionsResponse,
  TestSetsResponse,
  TestRunsResponse,
  TestJobStatus,
  CreateTestQuestionRequest,
  CreateTestSetRequest,
  UpdateTestSetRequest,
  CreateTestRunRequest,
  GenerateQuestionsRequest,
  // Feedback Admin types
  FeedbackAdminItem,
  FeedbackAdminStatsResponse,
} from '../types';
import { authService, authFetch } from './auth';

export const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8080';

/**
 * Get authorization headers if user is authenticated
 */
export function getAuthHeaders(): HeadersInit {
  const token = authService.getAccessToken();
  if (token) {
    return { 'Authorization': `Bearer ${token}` };
  }
  return {};
}

/**
 * API response for listing conversations
 */
interface ListConversationsResponse {
  conversations: StoredConversationData[];
  count: number;
}

/**
 * Stored conversation data as returned from the API
 */
interface StoredConversationData {
  id: string;
  sessionId: string;
  agentId: string;
  title: string;
  createdAt: string;
  updatedAt: string;
  messages: StoredMessageData[];
  communityLabel?: string;
  isArchived?: boolean;
  clientId?: string;
}

/**
 * Stored message data as returned from the API
 */
interface StoredMessageData {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
}

class ApiService {
  private baseUrl: string;

  constructor(baseUrl: string = API_BASE) {
    this.baseUrl = baseUrl;
  }

  async sendMessage(
    agentId: string,
    message: string,
    sessionId: string,
    communityLabel?: string,
    clientId?: string
  ): Promise<ChatResponse> {
    try {
      const response = await authFetch(`${this.baseUrl}/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          agent: agentId,
          message,
          sessionId,
          communityLabel,
          clientId,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Failed to send message:', error);
      throw error;
    }
  }

  async healthCheck(): Promise<boolean> {
    try {
      const response = await authFetch(`${this.baseUrl}/healthz`);
      return response.ok;
    } catch (error) {
      console.error('Health check failed:', error);
      return false;
    }
  }

  /** The backend's ENVIRONMENT (prod / stg / dev), or null if unreachable. */
  async getEnvironment(): Promise<string | null> {
    try {
      const response = await authFetch(`${this.baseUrl}/healthz`);
      if (!response.ok) return null;
      const data = await response.json();
      return typeof data.environment === 'string' ? data.environment : null;
    } catch {
      return null;
    }
  }

  // ============================================================================
  // Conversation Persistence API
  // ============================================================================

  /**
   * Fetch all stored conversations from the backend.
   * If authenticated, returns only the user's conversations.
   */
  async getConversations(
    limit: number = 50,
    offset: number = 0,
    includeArchived: boolean = false
  ): Promise<Conversation[]> {
    try {
      const includeArchivedParam = includeArchived ? '&include_archived=true' : '';
      const response = await authFetch(
        `${this.baseUrl}/conversations?limit=${limit}&offset=${offset}${includeArchivedParam}`,
        {
          headers: {
            ...getAuthHeaders(),
          },
        }
      );
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data: ListConversationsResponse = await response.json();

      // Convert stored format to frontend Conversation format
      return data.conversations.map(this.storedToConversation);
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
      return [];
    }
  }

  /**
   * Save a conversation to the backend.
   * If authenticated, associates the conversation with the current user.
   */
  async saveConversation(conversation: Conversation): Promise<Conversation | null> {
    try {
      const payload = this.conversationToStored(conversation);
      const response = await authFetch(`${this.baseUrl}/conversations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: StoredConversationData = await response.json();
      return this.storedToConversation(data);
    } catch (error) {
      console.error('Failed to save conversation:', error);
      return null;
    }
  }

  /**
   * Update a conversation's title.
   */
  async updateConversationTitle(conversationId: string, title: string): Promise<boolean> {
    try {
      const response = await authFetch(
        `${this.baseUrl}/conversations/${conversationId}/title`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders(),
          },
          body: JSON.stringify({ title }),
        }
      );
      return response.ok;
    } catch (error) {
      console.error('Failed to update conversation title:', error);
      return false;
    }
  }

  /**
   * Delete a conversation.
   */
  async deleteConversation(conversationId: string): Promise<boolean> {
    try {
      const response = await authFetch(
        `${this.baseUrl}/conversations/${conversationId}`,
        {
          method: 'DELETE',
          headers: {
            ...getAuthHeaders(),
          },
        }
      );
      return response.ok;
    } catch (error) {
      console.error('Failed to delete conversation:', error);
      return false;
    }
  }

  /**
   * Restore an archived conversation.
   */
  async unarchiveConversation(conversationId: string): Promise<boolean> {
    try {
      const response = await authFetch(
        `${this.baseUrl}/conversations/${conversationId}/unarchive`,
        {
          method: 'POST',
          headers: {
            ...getAuthHeaders(),
          },
        }
      );
      return response.ok;
    } catch (error) {
      console.error('Failed to unarchive conversation:', error);
      return false;
    }
  }

  // ============================================================================
  // Feedback API
  // ============================================================================

  /**
   * Submit feedback on an AI message.
   * Accepts text content and/or audio blob (audio will be transcribed server-side).
   */
  async submitFeedback(submission: FeedbackSubmission): Promise<Feedback | null> {
    try {
      const formData = new FormData();
      formData.append('conversationId', submission.conversationId);
      formData.append('messageSequence', submission.messageSequence.toString());
      formData.append('feedbackType', submission.feedbackType);

      if (submission.content) {
        formData.append('content', submission.content);
      }

      if (submission.audio) {
        formData.append('audio', submission.audio, 'feedback.webm');
      }

      const response = await authFetch(`${this.baseUrl}/feedback`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          // Don't set Content-Type - browser will set it with boundary for FormData
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      throw error;
    }
  }

  /**
   * Get list of feedback entries.
   * Regular users get their own feedback; admins get all.
   */
  async getFeedback(
    status?: string,
    limit: number = 50,
    offset: number = 0
  ): Promise<{ feedback: Feedback[]; count: number }> {
    try {
      const params = new URLSearchParams();
      params.append('limit', limit.toString());
      params.append('offset', offset.toString());
      if (status) {
        params.append('status', status);
      }

      const response = await authFetch(`${this.baseUrl}/feedback?${params}`, {
        headers: {
          ...getAuthHeaders(),
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to fetch feedback:', error);
      return { feedback: [], count: 0 };
    }
  }

  // ============================================================================
  // Conversion helpers
  // ============================================================================

  private storedToConversation(stored: StoredConversationData): Conversation {
    return {
      id: stored.id,
      sessionId: stored.sessionId,
      agentId: stored.agentId,
      title: stored.title,
      createdAt: new Date(stored.createdAt),
      updatedAt: new Date(stored.updatedAt),
      messages: stored.messages.map((msg) => ({
        id: msg.id,
        role: msg.role,
        content: msg.content,
        timestamp: new Date(msg.timestamp),
      })),
      communityLabel: stored.communityLabel,
      isArchived: stored.isArchived,
      clientId: stored.clientId,
    };
  }

  private conversationToStored(conversation: Conversation): StoredConversationData {
    return {
      id: conversation.id,
      sessionId: conversation.sessionId,
      agentId: conversation.agentId,
      title: conversation.title,
      createdAt: conversation.createdAt.toISOString(),
      updatedAt: conversation.updatedAt.toISOString(),
      messages: conversation.messages.map((msg) => ({
        id: msg.id,
        role: msg.role,
        content: msg.content,
        timestamp: msg.timestamp.toISOString(),
      })),
      communityLabel: conversation.communityLabel,
      isArchived: conversation.isArchived,
      clientId: conversation.clientId,
    };
  }

  // ============================================================================
  // RAG Ingest API
  // ============================================================================

  /**
   * Get list of all ingested documents.
   * Automatically paginates to fetch all documents if there are more than 200.
   */
  async getRagDocuments(): Promise<RagDocumentsResponse> {
    try {
      const limit = 200; // Max allowed by backend
      let offset = 0;
      let allDocuments: RagDocument[] = [];
      let lastResponse: RagDocumentsResponse | null = null;

      // Fetch all pages
      let hasMore = true;
      while (hasMore) {
        const response = await authFetch(
          `${this.baseUrl}/ingest/documents?limit=${limit}&offset=${offset}`,
          {
            headers: {
              ...getAuthHeaders(),
            },
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data: RagDocumentsResponse = await response.json();
        allDocuments = allDocuments.concat(data.documents);
        lastResponse = data;

        if (allDocuments.length >= data.total) {
          hasMore = false;
        } else {
          offset += limit;
        }
      }

      // Return combined response with all documents
      return {
        documents: allDocuments,
        count: allDocuments.length,
        total: lastResponse?.total ?? allDocuments.length,
        summary: lastResponse?.summary ?? { by_status: {}, by_type: {} },
      };
    } catch (error) {
      console.error('Failed to fetch RAG documents:', error);
      return { documents: [], count: 0, total: 0, summary: { by_status: {}, by_type: {} } };
    }
  }

  /**
   * Trigger document sync/reindex
   */
  async triggerRagSync(dryRun: boolean = false, maxCount?: number): Promise<RagSyncResponse> {
    try {
      const body: { dry_run: boolean; max_count?: number } = { dry_run: dryRun };
      if (maxCount !== undefined) {
        body.max_count = maxCount;
      }

      const response = await authFetch(`${this.baseUrl}/ingest/sync`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to trigger RAG sync:', error);
      throw error;
    }
  }

  /**
   * Get status of a sync job
   */
  async getSyncJobStatus(jobId: string): Promise<RagSyncJobStatus> {
    try {
      const response = await authFetch(`${this.baseUrl}/ingest/sync/${jobId}`, {
        method: 'GET',
        headers: {
          ...getAuthHeaders(),
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to get sync job status:', error);
      throw error;
    }
  }

  /**
   * Test search with custom weights and optional semantic reranking
   */
  async testRagSearch(
    query: string,
    vectorWeight: number = 0.7,
    keywordWeight: number = 0.3,
    limit: number = 5,
    useSemanticRanker: boolean = false
  ): Promise<RagSearchResponse> {
    try {
      const response = await authFetch(`${this.baseUrl}/ingest/search`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          query,
          vector_weight: vectorWeight,
          keyword_weight: keywordWeight,
          top_k: limit,
          use_semantic_ranker: useSemanticRanker,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to perform RAG search:', error);
      throw error;
    }
  }

  /**
   * Delete a document from the index
   */
  async deleteRagDocument(documentId: string): Promise<boolean> {
    try {
      const response = await authFetch(`${this.baseUrl}/ingest/documents/${documentId}`, {
        method: 'DELETE',
        headers: {
          ...getAuthHeaders(),
        },
      });

      return response.ok;
    } catch (error) {
      console.error('Failed to delete RAG document:', error);
      return false;
    }
  }

  /**
   * Start a retry job for a document (returns job_id immediately)
   */
  async startRetryJob(documentId: string): Promise<{ job_id: string; status: string } | null> {
    console.log('[API] startRetryJob called for:', documentId);
    try {
      const url = `${this.baseUrl}/ingest/documents/${documentId}/retry`;
      const response = await authFetch(url, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[API] Start retry failed:', response.status, errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('[API] Retry job started:', result);
      return result;
    } catch (error) {
      console.error('[API] Failed to start retry job:', error);
      return null;
    }
  }

  /**
   * Get retry job status
   */
  async getRetryJobStatus(jobId: string): Promise<{
    status: string;
    document_id?: string;
    filename?: string;
    result?: unknown;
    error?: string;
  } | null> {
    try {
      const response = await authFetch(`${this.baseUrl}/ingest/retry/${jobId}`, {
        headers: {
          ...getAuthHeaders(),
        },
      });

      if (!response.ok) {
        return null;
      }

      return await response.json();
    } catch (error) {
      console.error('[API] Failed to get retry job status:', error);
      return null;
    }
  }

  /**
   * Retry indexing a document and wait for completion
   * Polls the job status until done
   */
  async retryRagDocument(
    documentId: string,
    onStatusChange?: (status: string, filename?: string) => void
  ): Promise<{ success: boolean; result?: unknown; error?: string }> {
    console.log('[API] retryRagDocument called for:', documentId);

    // Start the job
    const startResult = await this.startRetryJob(documentId);
    if (!startResult?.job_id) {
      return { success: false, error: 'Failed to start retry job' };
    }

    const jobId = startResult.job_id;
    onStatusChange?.('pending');

    // Poll for completion
    const maxAttempts = 120; // 2 minutes max
    const pollInterval = 1000; // 1 second

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      await new Promise((resolve) => setTimeout(resolve, pollInterval));

      const status = await this.getRetryJobStatus(jobId);
      if (!status) {
        continue;
      }

      onStatusChange?.(status.status, status.filename);

      if (status.status === 'completed') {
        console.log('[API] Retry completed:', status.result);
        return { success: true, result: status.result };
      }

      if (status.status === 'failed') {
        console.error('[API] Retry failed:', status.error);
        return { success: false, error: status.error };
      }
    }

    return { success: false, error: 'Retry timed out' };
  }

  /**
   * Get RAG dashboard stats
   */
  async getRagStats(): Promise<RagStatsResponse> {
    try {
      const response = await authFetch(`${this.baseUrl}/ingest/stats`, {
        headers: {
          ...getAuthHeaders(),
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to fetch RAG stats:', error);
      // Return default stats
      return {
        total_documents: 0,
        indexed_documents: 0,
        processing_documents: 0,
        failed_documents: 0,
        pending_documents: 0,
        stale_processing: 0,
        total_chunks: 0,
        total_storage_bytes: 0,
        documents_by_type: {},
        documents_by_status: {},
      };
    }
  }

  /**
   * Get RAG configuration settings
   */
  async getRagConfig(): Promise<RagConfigResponse> {
    try {
      const response = await authFetch(`${this.baseUrl}/ingest/config`, {
        headers: {
          ...getAuthHeaders(),
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to fetch RAG config:', error);
      throw error;
    }
  }

  /**
   * Get RAG cost statistics
   */
  async getRagCosts(days: number = 30): Promise<RagCostsResponse> {
    try {
      const response = await authFetch(`${this.baseUrl}/ingest/costs?days=${days}`, {
        headers: {
          ...getAuthHeaders(),
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to fetch RAG costs:', error);
      // Return empty costs
      return {
        period_days: days,
        total_cost_usd: 0,
        total_input_tokens: 0,
        total_output_tokens: 0,
        total_api_calls: 0,
        costs_by_operation: {},
        daily_costs: [],
        top_documents: [],
        pricing_warnings: 0,
      };
    }
  }

  /**
   * Get cost details for a specific document
   */
  async getDocumentCosts(documentId: string): Promise<RagDocumentCostsResponse> {
    try {
      const response = await authFetch(`${this.baseUrl}/ingest/costs/${documentId}`, {
        headers: {
          ...getAuthHeaders(),
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to fetch document costs:', error);
      throw error;
    }
  }

  /**
   * Get document chunks with optional filtering
   */
  async getRagChunks(documentId?: string, limit: number = 100, offset: number = 0): Promise<RagChunksResponse> {
    try {
      const params = new URLSearchParams();
      params.append('limit', limit.toString());
      params.append('offset', offset.toString());
      if (documentId) {
        params.append('document_id', documentId);
      }

      const response = await authFetch(`${this.baseUrl}/ingest/chunks?${params}`, {
        headers: {
          ...getAuthHeaders(),
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to fetch RAG chunks:', error);
      return { chunks: [], total_count: 0 };
    }
  }

  /**
   * Get the exact format a chunk would have when returned to the LLM.
   * Uses the same formatting function as the search tool, ensuring admins
   * see exactly what the LLM would see if this chunk were returned.
   */
  async getChunkLlmFormat(chunkId: string): Promise<{ chunk_id: string; llm_format: string; metadata: Record<string, unknown> } | null> {
    try {
      const response = await authFetch(`${this.baseUrl}/ingest/chunks/${chunkId}/llm-format`, {
        headers: {
          ...getAuthHeaders(),
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to fetch chunk LLM format:', error);
      return null;
    }
  }

  /**
   * Get chunk info by Azure Search ID.
   * Used to resolve citation references to document/chunk information.
   */
  async getChunkByAzureSearchId(azureSearchId: string): Promise<{
    id: string;
    document_id: string;
    chunk_index: number;
    azure_search_id: string;
    filename: string;
    page_number?: number;
    heading_context?: string;
    content_type?: string;
  } | null> {
    try {
      const response = await authFetch(`${this.baseUrl}/ingest/chunks/by-search-id/${encodeURIComponent(azureSearchId)}`, {
        headers: {
          ...getAuthHeaders(),
        },
      });

      if (!response.ok) {
        // Gracefully handle 404 - chunk may have been deleted
        if (response.status === 404) {
          console.warn(`Chunk not found for azure_search_id: ${azureSearchId}`);
          return null;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to fetch chunk by azure_search_id:', error);
      return null;
    }
  }

  /**
   * Get detailed document information including chunks
   */
  async getRagDocumentDetails(documentId: string, includeChunks: boolean = true): Promise<RagDocumentDetails | null> {
    try {
      const params = new URLSearchParams();
      params.append('include_chunks', includeChunks.toString());

      const response = await authFetch(`${this.baseUrl}/ingest/documents/${documentId}?${params}`, {
        headers: {
          ...getAuthHeaders(),
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to fetch RAG document details:', error);
      return null;
    }
  }

  /**
   * Get document file for viewing/download
   */
  async getRagDocumentFile(documentId: string): Promise<Blob | null> {
    try {
      const response = await authFetch(`${this.baseUrl}/ingest/documents/${documentId}/file`, {
        headers: {
          ...getAuthHeaders(),
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.blob();
    } catch (error) {
      console.error('Failed to fetch RAG document file:', error);
      return null;
    }
  }

  // ============================================================================
  // RAG Testing API
  // ============================================================================

  /**
   * Get all test questions
   */
  async getTestQuestions(limit: number = 100, offset: number = 0): Promise<TestQuestionsResponse> {
    try {
      const params = new URLSearchParams();
      params.append('limit', limit.toString());
      params.append('offset', offset.toString());

      const response = await authFetch(`${this.baseUrl}/testing/questions?${params}`, {
        headers: {
          ...getAuthHeaders(),
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to fetch test questions:', error);
      return { questions: [], total: 0 };
    }
  }

  /**
   * Create a new test question
   */
  async createTestQuestion(request: CreateTestQuestionRequest): Promise<TestQuestion | null> {
    try {
      const response = await authFetch(`${this.baseUrl}/testing/questions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to create test question:', error);
      throw error;
    }
  }

  /**
   * Delete a test question
   */
  async deleteTestQuestion(questionId: string): Promise<boolean> {
    try {
      const response = await authFetch(`${this.baseUrl}/testing/questions/${questionId}`, {
        method: 'DELETE',
        headers: {
          ...getAuthHeaders(),
        },
      });

      return response.ok;
    } catch (error) {
      console.error('Failed to delete test question:', error);
      return false;
    }
  }

  /**
   * Get all test sets
   */
  async getTestSets(): Promise<TestSetsResponse> {
    try {
      const response = await authFetch(`${this.baseUrl}/testing/sets`, {
        headers: {
          ...getAuthHeaders(),
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to fetch test sets:', error);
      return { test_sets: [] };
    }
  }

  /**
   * Create a new test set
   */
  async createTestSet(request: CreateTestSetRequest): Promise<TestSet | null> {
    try {
      const response = await authFetch(`${this.baseUrl}/testing/sets`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to create test set:', error);
      throw error;
    }
  }

  /**
   * Get a test set with its questions
   */
  async getTestSet(testSetId: string): Promise<TestSetWithQuestions | null> {
    try {
      const response = await authFetch(`${this.baseUrl}/testing/sets/${testSetId}`, {
        headers: {
          ...getAuthHeaders(),
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to fetch test set:', error);
      return null;
    }
  }

  /**
   * Update a test set (add/remove questions)
   */
  async updateTestSet(testSetId: string, request: UpdateTestSetRequest): Promise<TestSet | null> {
    try {
      const response = await authFetch(`${this.baseUrl}/testing/sets/${testSetId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to update test set:', error);
      throw error;
    }
  }

  /**
   * Delete a test set
   */
  async deleteTestSet(testSetId: string): Promise<boolean> {
    try {
      const response = await authFetch(`${this.baseUrl}/testing/sets/${testSetId}`, {
        method: 'DELETE',
        headers: {
          ...getAuthHeaders(),
        },
      });

      return response.ok;
    } catch (error) {
      console.error('Failed to delete test set:', error);
      return false;
    }
  }

  /**
   * Generate questions from documents for a test set
   */
  async generateTestQuestions(
    testSetId: string,
    request: GenerateQuestionsRequest
  ): Promise<{ job_id: string; message: string } | null> {
    try {
      const response = await authFetch(`${this.baseUrl}/testing/sets/${testSetId}/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to generate test questions:', error);
      throw error;
    }
  }

  /**
   * Get all test runs, optionally filtered by test set
   */
  async getTestRuns(testSetId?: string, limit: number = 50, offset: number = 0): Promise<TestRunsResponse> {
    try {
      const params = new URLSearchParams();
      params.append('limit', limit.toString());
      params.append('offset', offset.toString());
      if (testSetId) {
        params.append('test_set_id', testSetId);
      }

      const response = await authFetch(`${this.baseUrl}/testing/runs?${params}`, {
        headers: {
          ...getAuthHeaders(),
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to fetch test runs:', error);
      return { test_runs: [], total: 0 };
    }
  }

  /**
   * Create and start a new test run
   */
  async createTestRun(request: CreateTestRunRequest): Promise<{ job_id: string; run_id: string; message: string } | null> {
    try {
      const response = await authFetch(`${this.baseUrl}/testing/runs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to create test run:', error);
      throw error;
    }
  }

  /**
   * Get a test run with results
   */
  async getTestRun(runId: string): Promise<TestRunWithResults | null> {
    try {
      const response = await authFetch(`${this.baseUrl}/testing/runs/${runId}?include_results=true`, {
        headers: {
          ...getAuthHeaders(),
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to fetch test run:', error);
      return null;
    }
  }

  /**
   * Delete a test run
   */
  async deleteTestRun(runId: string): Promise<boolean> {
    try {
      const response = await authFetch(`${this.baseUrl}/testing/runs/${runId}`, {
        method: 'DELETE',
        headers: {
          ...getAuthHeaders(),
        },
      });

      return response.ok;
    } catch (error) {
      console.error('Failed to delete test run:', error);
      return false;
    }
  }

  /**
   * Get status of a test job (question generation or test execution)
   */
  async getTestJobStatus(jobId: string): Promise<TestJobStatus | null> {
    try {
      const response = await authFetch(`${this.baseUrl}/testing/jobs/${jobId}`, {
        headers: {
          ...getAuthHeaders(),
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to fetch test job status:', error);
      return null;
    }
  }

  // ==========================================================================
  // Feedback Admin API
  // ==========================================================================

  /**
   * List feedback items with their corrections for admin review
   */
  async listAdminFeedback(params?: {
    status?: string;
    correction_type?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ feedback: FeedbackAdminItem[]; count: number; total: number } | null> {
    try {
      const searchParams = new URLSearchParams();
      if (params?.status) searchParams.append('status', params.status);
      if (params?.correction_type) searchParams.append('correction_type', params.correction_type);
      if (params?.limit) searchParams.append('limit', params.limit.toString());
      if (params?.offset) searchParams.append('offset', params.offset.toString());

      const url = `${this.baseUrl}/admin/feedback${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;
      const response = await authFetch(url, {
        headers: {
          ...getAuthHeaders(),
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      // API now returns snake_case, map directly to frontend types
      return {
        feedback: data.feedback.map((item: FeedbackAdminItem) => ({
          id: item.id,
          user_id: item.user_id,
          user_email: item.user_email,
          conversation_id: item.conversation_id,
          message_sequence: item.message_sequence,
          feedback_type: item.feedback_type,
          content: item.content,
          status: item.status,
          correction_type: item.correction_type,
          conversation_context: item.conversation_context ? {
            user_message: item.conversation_context.user_message,
            ai_response: item.conversation_context.ai_response,
            source_document: item.conversation_context.source_document,
            chunk_id: item.conversation_context.chunk_id,
            rag_results: item.conversation_context.rag_results,
          } : undefined,
          ai_analysis: item.ai_analysis ? {
            summary: item.ai_analysis.summary,
            confidence: item.ai_analysis.confidence * 100, // Convert to percentage
            correction_type: item.ai_analysis.correction_type,
            rag_correction: item.ai_analysis.rag_correction,
            prompt_correction: item.ai_analysis.prompt_correction,
          } : undefined,
          admin_notes: item.admin_notes,
          created_at: item.created_at,
          updated_at: item.updated_at,
        })),
        count: data.count,
        total: data.total,
      };
    } catch (error) {
      console.error('Failed to list admin feedback:', error);
      return null;
    }
  }

  /**
   * Get feedback statistics for admin dashboard
   */
  async getFeedbackStats(): Promise<FeedbackAdminStatsResponse | null> {
    try {
      const response = await authFetch(`${this.baseUrl}/admin/feedback/stats`, {
        headers: {
          ...getAuthHeaders(),
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return {
        total: data.total,
        pending: data.pending,
        analyzed: data.analyzed || 0,  // AI generated, ready for review
        analyzing: data.analyzing || 0,  // Legacy
        revising: data.revising,
        approved: data.approved || 0,
        applying: data.applying || 0,
        reviewed: data.reviewed || 0,  // Legacy
        resolved: data.resolved,
        dismissed: data.dismissed,
        validation_failed: data.validation_failed || 0,
        apply_failed: data.apply_failed || 0,
        by_correction_type: {
          rag: data.byCorrectionType?.rag || 0,
          prompt: data.byCorrectionType?.prompt || 0,
          null: data.byCorrectionType?.null || 0,
        },
      };
    } catch (error) {
      console.error('Failed to get feedback stats:', error);
      return null;
    }
  }

  /**
   * Get detailed feedback item with correction history
   */
  async getAdminFeedbackDetail(feedbackId: string): Promise<{ feedback: FeedbackAdminItem; corrections: Record<string, unknown>[] } | null> {
    try {
      const response = await authFetch(`${this.baseUrl}/admin/feedback/${feedbackId}`, {
        headers: {
          ...getAuthHeaders(),
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to get feedback detail:', error);
      return null;
    }
  }

  /**
   * Request a revision of the current correction
   */
  async requestFeedbackRevision(feedbackId: string, message: string): Promise<Record<string, unknown> | null> {
    try {
      const response = await authFetch(`${this.baseUrl}/admin/feedback/${feedbackId}/request-revision`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({ message }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to request revision:', error);
      return null;
    }
  }

  /**
   * Approve and apply the current correction
   */
  async approveFeedbackCorrection(feedbackId: string, notes?: string): Promise<Record<string, unknown> | null> {
    try {
      const response = await authFetch(`${this.baseUrl}/admin/feedback/${feedbackId}/approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({ notes }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to approve correction:', error);
      return null;
    }
  }

  /**
   * Dismiss the feedback (no correction needed)
   */
  async dismissFeedback(feedbackId: string, reason?: string): Promise<Record<string, unknown> | null> {
    try {
      const response = await authFetch(`${this.baseUrl}/admin/feedback/${feedbackId}/dismiss`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({ reason }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to dismiss feedback:', error);
      return null;
    }
  }

  /**
   * Generate or regenerate an AI correction for feedback
   */
  async generateFeedbackCorrection(feedbackId: string, adminFeedback?: string): Promise<FeedbackAdminItem | null> {
    try {
      const response = await authFetch(`${this.baseUrl}/admin/feedback/${feedbackId}/generate-correction`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({ admin_feedback: adminFeedback }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to generate correction:', error);
      return null;
    }
  }
}

export const apiService = new ApiService();
